import React, { Component, ReactNode } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  navigate: NavigateFunction;
  fallbackPath?: string;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (this.state.hasError && !prevState.hasError) {
      // Redirect to fallback path (default: /home) when error occurs
      const fallbackPath = this.props.fallbackPath || '/home';
      console.warn(`Redirecting to ${fallbackPath} due to error`);
      this.props.navigate(fallbackPath, { replace: true });
      
      // Reset error state after navigation
      setTimeout(() => {
        this.setState({ hasError: false });
      }, 100);
    }
  }

  render() {
    if (this.state.hasError) {
      // Show minimal loading state while redirecting
      return (
        <div className="flex flex-col min-h-screen w-full">
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Redirecting...</p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC to wrap with useNavigate hook
export const withNavigate = (Component: typeof ErrorBoundary) => {
  return (props: Omit<Props, 'navigate'>) => {
    const navigate = useNavigate();
    return <Component {...props} navigate={navigate} />;
  };
};

export const ErrorBoundaryWithNavigate = withNavigate(ErrorBoundary);

export default ErrorBoundary;

